import './PlanInfo.less';

import React, { useState } from 'react';
import { Button } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import {
  convertSubscriptionDataToStripeSubscription,
  getSubscriptionStatusLabel,
  openCheckoutForPlan,
  openPricingPageOrSubscriptionPortal,
} from '../../services/subscription';
import {
  SubscriptionData,
  SubscriptionStatus,
} from '../../interfaces/subscription';
import { Notify } from '../../services/notificationService';
import { Resource } from '../../enums/common';
import { useAbility } from '../../hooks/useAbility';
import PlanSelectionModal from '../PlanSelectionModal/PlanSelectionModal';
import { PRODUCT_PRO_PLAN, PRODUCT_TEAM_PLAN } from '../PreviewInvoice/helpers';

interface PlanInfoProps {
  subscription: SubscriptionData;
  upgradeable: boolean;
  portalOpenable: boolean;
}

const PlanInfo: React.FC<PlanInfoProps> = ({
  subscription,
  upgradeable,
  portalOpenable,
}) => {
  const { currentAbility } = useAbility();
  const { products } = useSelector((state: RootState) => state.products);
  const currentTeam = useSelector(
    (state: RootState) => state.team.currentTeam.record
  );
  // Derive team context from the team slice — state.access.isTeamUser is never populated
  const isTeamContext = !!currentTeam;
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [activating, setActivating] = useState(false);

  // Add null checks to prevent runtime errors
  if (!subscription) {
    return <span>Loading...</span>;
  }

  const hasSubscriptionPermission = currentAbility.can(
    'manage',
    Resource.Subscription,
    convertSubscriptionDataToStripeSubscription(subscription)
  );

  // Prioritize problematic subscription statuses over freePlan flag
  // This ensures past due, expired, etc. are shown correctly
  const problematicStatuses = [
    SubscriptionStatus.PAST_DUE,
    SubscriptionStatus.EXPIRED,
    SubscriptionStatus.INACTIVE,
    SubscriptionStatus.INCOMPLETE,
    SubscriptionStatus.PAUSED,
  ];

  const status = problematicStatuses.includes(subscription.status)
    ? subscription.status
    : subscription.freePlan
      ? SubscriptionStatus.FREE
      : subscription.status;
  const isFreePlan = status === SubscriptionStatus.FREE;
  const isStripeDataCorrupted =
    subscription.status === SubscriptionStatus.INVALID;
  // Check actual Stripe payment method rather than trialEndBehavior,
  // which is just a configuration for what happens when the trial ends
  const noPaymentMethodSet = subscription.rawData
    ? !subscription.rawData.default_payment_method
    : !subscription.trialEndBehavior ||
      subscription.trialEndBehavior === 'cancel';
  const isUnconvertedTrial =
    status === SubscriptionStatus.TRIALING && noPaymentMethodSet;

  // Only show the plan modal if the context-relevant product is loaded.
  // Must match PlanSelectionModal's filtering (team → PRODUCT_TEAM_PLAN, personal → PRODUCT_PRO_PLAN).
  const relevantProductId = isTeamContext
    ? PRODUCT_TEAM_PLAN
    : PRODUCT_PRO_PLAN;
  const canShowPlanModal = products.some((p) => p.id === relevantProductId);

  // Sub-teams cannot manage their own subscription — only top-level teams can
  const isSubTeam = isTeamContext && !!currentTeam?.master_organization_id;

  const getStatusClass = (status: SubscriptionStatus): string => {
    // Map status to CSS class names
    switch (status) {
      case SubscriptionStatus.ACTIVE:
        return 'active';
      case SubscriptionStatus.TRIALING:
        return 'trial';
      case SubscriptionStatus.FREE:
      case SubscriptionStatus.INACTIVE:
      case SubscriptionStatus.INCOMPLETE:
      case SubscriptionStatus.EXPIRED:
      case SubscriptionStatus.PAUSED:
      case SubscriptionStatus.PAST_DUE:
      case SubscriptionStatus.UNKNOWN:
      case SubscriptionStatus.NOT_FOUND:
      case SubscriptionStatus.NOT_SUBSCRIBED:
      case SubscriptionStatus.INVALID:
        return status.toLowerCase();
      default:
        return 'unknown';
    }
  };

  const handleSubscriptionClick = async (
    flowType?: 'activate_trial',
    priceId?: string
  ): Promise<boolean> => {
    try {
      // Tokens are already available via TokenService (set by AuthenticationGuard)
      // No need to refresh - the auth gate ensures tokens are ready before any API calls
      return await openPricingPageOrSubscriptionPortal(
        subscription.status,
        flowType,
        priceId
      );
    } catch (error) {
      Notify.error(
        (error as Error).message || 'Failed to open subscription portal'
      );
      return false;
    }
  };

  const handlePlanSelected = async (priceId: string) => {
    if (activating) return;
    setActivating(true);
    try {
      if (isFreePlan) {
        // Free plan users have no Stripe subscription yet — use Checkout to create one.
        // Same-tab redirect; on return, useCheckoutSuccessHandler refreshes state.
        const success = await openCheckoutForPlan(priceId);
        if (!success) {
          // Checkout failed (e.g. network error) — keep modal open so user can retry
          return;
        }
        // On success, the page navigates to Stripe Checkout (same-tab redirect)
      } else if (isUnconvertedTrial) {
        // Trial users already have a Stripe subscription (without payment method).
        // Stripe Checkout rejects customers with existing subscriptions, so use
        // the Billing Portal to add a payment method and switch to the selected plan.
        const success = await handleSubscriptionClick(
          'activate_trial',
          priceId
        );
        if (!success) {
          // Portal failed to open — keep modal open so user can retry
          return;
        }
      }
      setShowPlanModal(false);
    } catch (error) {
      Notify.error((error as Error).message || 'Failed to update subscription');
    } finally {
      setActivating(false);
    }
  };

  return (
    <div className="plan-info">
      <span className={`status-tag ${getStatusClass(status)}`}>
        {getSubscriptionStatusLabel(status) || 'Loading...'}
      </span>
      {upgradeable &&
        (isFreePlan || isUnconvertedTrial) &&
        hasSubscriptionPermission &&
        !isSubTeam && (
          <>
            &nbsp;-&nbsp;
            <Button
              className="plan-info-upgrade-plan-btn"
              type="link"
              style={{ padding: 0 }}
              onClick={() => {
                if (canShowPlanModal) {
                  setShowPlanModal(true);
                } else if (isUnconvertedTrial) {
                  handleSubscriptionClick('activate_trial');
                } else {
                  handleSubscriptionClick();
                }
              }}
            >
              Activate subscription
            </Button>
          </>
        )}

      {portalOpenable &&
        !isStripeDataCorrupted &&
        !isFreePlan &&
        !isUnconvertedTrial &&
        !isSubTeam && (
          <>
            &nbsp;-&nbsp;
            <Button
              className="plan-info-change-plan-btn"
              type="link"
              style={{ padding: 0 }}
              onClick={() => handleSubscriptionClick()}
            >
              Manage subscription
            </Button>
          </>
        )}

      <PlanSelectionModal
        open={showPlanModal}
        onSelect={handlePlanSelected}
        onCancel={() => setShowPlanModal(false)}
        loading={activating}
        title="Activate your subscription"
      />
    </div>
  );
};

export default PlanInfo;
